// Биллинг — тариф/оплата для панели «Тариф и оплата» (ANX-060, слой 4).
// Бэкенд: GET /me/billing (read-модель) + POST /billing/{subscribe,confirm,cancel}.
import type { ApiClient } from './client';

export interface BillingLimits {
  seats: number; // 0 = безлимит
  extraSeatsAllowed: boolean;
  dialogsPerMonth: number; // 0 = безлимит
  teamFeatures: boolean;
  brandingHidden: boolean;
}

export interface BillingUsage {
  seatsUsed: number;
  dialogsThisMonth: number;
}

export interface BillingPrice {
  rubMinor: number;
  extraSeatRubMinor: number;
}

export interface Billing {
  plan: string;
  status: string; // none | pending | active | canceled | past_due
  currentPeriodEnd: string | null;
  limits: BillingLimits;
  usage: BillingUsage;
  price: BillingPrice;
}

interface RawBilling {
  plan: string;
  status: string;
  current_period_end: string | null;
  limits: {
    seats: number;
    extra_seats_allowed: boolean;
    dialogs_per_month: number;
    team_features: boolean;
    branding_hidden: boolean;
  };
  usage: { seats_used: number; dialogs_this_month: number };
  price: { rub_minor: number; extra_seat_rub_minor: number };
}

function toBilling(r: RawBilling): Billing {
  return {
    plan: r.plan,
    status: r.status,
    currentPeriodEnd: r.current_period_end ?? null,
    limits: {
      seats: r.limits.seats,
      extraSeatsAllowed: r.limits.extra_seats_allowed,
      dialogsPerMonth: r.limits.dialogs_per_month,
      teamFeatures: r.limits.team_features,
      brandingHidden: r.limits.branding_hidden,
    },
    usage: {
      seatsUsed: r.usage.seats_used,
      dialogsThisMonth: r.usage.dialogs_this_month,
    },
    price: {
      rubMinor: r.price.rub_minor,
      extraSeatRubMinor: r.price.extra_seat_rub_minor,
    },
  };
}

export async function getBilling(client: ApiClient): Promise<Billing> {
  const raw = await client.request<RawBilling>('/api/v1/me/billing');
  return toBilling(raw);
}

export interface SubscribeResult {
  paymentUrl: string;
  status: string;
}

// subscribe — создаёт у провайдера подписку на платный план и возвращает ссылку
// на первый платёж (привязку карты). Панель редиректит на paymentUrl; по
// возврату зовёт confirmSubscription. Пока только billing_mode=card (счёт-акт —
// 060b/слой 5).
export async function subscribe(client: ApiClient, plan: string): Promise<SubscribeResult> {
  const raw = await client.request<{ payment_url: string; status: string }>(
    '/api/v1/billing/subscribe',
    { method: 'POST', json: { plan, billing_mode: 'card' } },
  );
  return { paymentUrl: raw.payment_url, status: raw.status };
}

export async function confirmSubscription(client: ApiClient): Promise<{ status: string }> {
  return client.request<{ status: string }>('/api/v1/billing/confirm', { method: 'POST' });
}

export async function cancelSubscription(client: ApiClient): Promise<{ status: string }> {
  return client.request<{ status: string }>('/api/v1/billing/cancel', { method: 'POST' });
}

// ---- Счёт-акт (безнал РФ, ANX-060b) ---------------------------------------

export interface Requisites {
  kind: string; // legal | ip
  name: string;
  inn: string;
  kpp: string;
  address: string;
  email: string;
}

export async function getRequisites(client: ApiClient): Promise<Requisites> {
  const r = await client.request<Partial<Requisites>>('/api/v1/billing/requisites');
  return {
    kind: r.kind || 'legal',
    name: r.name ?? '',
    inn: r.inn ?? '',
    kpp: r.kpp ?? '',
    address: r.address ?? '',
    email: r.email ?? '',
  };
}

export async function putRequisites(client: ApiClient, input: Requisites): Promise<Requisites> {
  return client.request<Requisites>('/api/v1/billing/requisites', {
    method: 'PUT',
    json: input,
  });
}

export interface InvoiceSummary {
  id: string;
  number: string;
  plan: string;
  periodStart: string;
  periodEnd: string;
  amountMinor: number;
  currency: string;
  status: string; // issued | paid | canceled
  issuedAt: string;
  paidAt: string | null;
}

interface RawInvoice {
  id: string;
  number: string;
  plan: string;
  period_start: string;
  period_end: string;
  amount_minor: number;
  currency: string;
  status: string;
  issued_at: string;
  paid_at: string | null;
}

function toInvoice(r: RawInvoice): InvoiceSummary {
  return {
    id: r.id,
    number: r.number,
    plan: r.plan,
    periodStart: r.period_start,
    periodEnd: r.period_end,
    amountMinor: r.amount_minor,
    currency: r.currency,
    status: r.status,
    issuedAt: r.issued_at,
    paidAt: r.paid_at ?? null,
  };
}

export async function issueInvoice(client: ApiClient, plan: string): Promise<InvoiceSummary> {
  const raw = await client.request<RawInvoice>('/api/v1/billing/invoice', {
    method: 'POST',
    json: { plan },
  });
  return toInvoice(raw);
}

export async function listInvoices(client: ApiClient): Promise<InvoiceSummary[]> {
  const raw = await client.request<{ invoices: RawInvoice[] }>('/api/v1/billing/invoices');
  return (raw.invoices ?? []).map(toInvoice);
}

export async function markInvoicePaid(client: ApiClient, id: string): Promise<InvoiceSummary> {
  const raw = await client.request<RawInvoice>(`/api/v1/billing/invoices/${id}/mark-paid`, {
    method: 'POST',
  });
  return toInvoice(raw);
}

// fetchInvoicePdf — авторизованная загрузка PDF (gateway отдаёт под Bearer, не
// presigned URL как exports), возвращает Blob для открытия/скачивания.
export async function fetchInvoicePdf(
  origin: string,
  token: string,
  id: string,
  kind: 'invoice' | 'act',
): Promise<Blob> {
  const q = kind === 'act' ? '?doc=act' : '';
  const res = await fetch(`${origin}/api/v1/billing/invoices/${id}/pdf${q}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`invoice pdf: ${res.status}`);
  return res.blob();
}
